/* Povelio cart drawer: cross-category suggestion carousel with in-drawer variant
   choice, ported from the approved Nirole drawer.

   Nirole runs Impulse and hooks theme.routes plus Impulse's cart:build /
   cart:updated / cart:open / cart:close events. Denim is Dawn based: it exposes
   window.routes, dispatches cart:updated and cart:open on document, and repaints
   the whole .drawer__inner from the cart-drawer section on every cart change.
   So this file wires to Denim's own API and re-mounts itself after each repaint.

   Purely additive. If anything here throws, Denim's drawer keeps working. */
(function () {
  'use strict';

  var D = window.PovCartData;
  if (!D) return;

  var ROTATE_MS = 4000;
  var TRUST = ['Secure checkout', '30-day returns', 'UK support'];
  var REASSURE = 'Free UK delivery on every order';

  var suggestEl = null;
  var list = [], idx = 0, timer = null, manual = false;
  var sel = [];            // chosen option values for the visible suggestion
  var cache = {};          // handle -> product JSON
  var busy = false;
  var lastCart = null;
  var writing = false;   // true while we are writing our own DOM, so the
                         // MutationObserver does not react to itself
  var menuOpen = false;  // a variant <select> in the card has its popup open
  var pendingRender = false;
  var deferredList = null;   // suggestion list held back while the card is in use

  // A native select popup is owned by its DOM node and cannot be inspected, so
  // the open state is tracked from the events around it. Focus is the reliable
  // half (an open popup always has focus); the pointer flag covers the gap on
  // browsers that fire focus late.
  function menuBusy() {
    if (menuOpen) return true;
    var a = document.activeElement;
    return !!(a && a.tagName === 'SELECT' && a.closest && a.closest('#PovSuggest'));
  }

  // The carousel tick advances idx and only then asks for a paint. If the
  // shopper opens the dropdown in the gap between those two, the paint is
  // correctly deferred but idx has already moved, so state and DOM disagree and
  // the next render would swap the product out from under the open menu. Any
  // interaction with the visible card re-anchors idx to what is actually shown.
  function syncIdxToCard() {
    var card = document.querySelector('#PovSuggest .psg__card');
    var handle = card && card.getAttribute('data-handle');
    if (!handle) return;
    for (var i = 0; i < list.length; i++) {
      if (list[i].h === handle) { idx = i; return; }
    }
  }

  function releaseMenu() {
    if (!menuOpen) return;
    menuOpen = false;
    // A shopper who has configured an option keeps that card until they add it
    // or the cart changes under them, so a held list is only adopted when they
    // walked away without choosing anything.
    if (deferredList && !manual) { applyList(deferredList); deferredList = null; }
    if (pendingRender) { pendingRender = false; renderSuggest(); }
    if (!manual) start();
  }

  /* ---------------------------------------------------------------- utils */
  function routes() {
    var r = window.routes || {};
    return {
      cart: (r.cart_url || '/cart') + '.js',
      add: r.cart_add_url || '/cart/add'
    };
  }

  // Denim does not guarantee Shopify.formatMoney on every page, so the shop's
  // own money format is rendered into window.PovCartConfig by the drawer snippet.
  function money(cents) {
    var fmt = (window.PovCartConfig && window.PovCartConfig.moneyFormat) || '£{{amount}}';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var kind = m ? m[1] : 'amount';
    var n = Number(cents) / 100;
    var body;
    if (kind === 'amount_no_decimals') body = String(Math.round(n));
    else if (kind === 'amount_with_comma_separator') body = n.toFixed(2).replace('.', ',');
    else if (kind === 'amount_no_decimals_with_comma_separator') body = String(Math.round(n));
    else body = n.toFixed(2);
    // thousands separators
    var parts = body.split(/[.,]/);
    var sep = /comma_separator/.test(kind) ? '.' : ',';
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + sep);
    body = parts.length > 1 ? parts[0] + (/comma_separator/.test(kind) ? ',' : '.') + parts[1] : parts[0];
    return m ? fmt.replace(m[0], body) : fmt + body;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function getProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) cache[handle] = p; return p; });
  }

  // cart.total_price is POST discount. Anything compared against a spend
  // threshold has to use the pre-discount subtotal or the maths lies.
  function subtotal(cart) {
    return cart.items_subtotal_price != null ? cart.items_subtotal_price
      : cart.original_total_price != null ? cart.original_total_price
      : cart.total_price;
  }

  /* ----------------------------------------------------- header + footer copy */
  function renderChrome(cart) {
    var head = document.querySelector('#CartDrawer .drawer__heading');
    if (head) {
      var base = head.getAttribute('data-pov-base');
      if (!base) {
        // Denim's own settings.cart_title already substitutes the live count via
        // [x], so headings arrive as "Cart" or "Cart - 1" or "Cart (1)". Strip any
        // trailing separator and number first or the counts stack up.
        base = head.textContent
          .replace(/\s*[•·|:,\-–]?\s*\(?\s*\d+\s*\)?\s*$/, '')
          .trim() || 'Cart';
        head.setAttribute('data-pov-base', base);
      }
      // item_count is TOTAL UNITS. items.length would be the number of distinct
      // lines, which is what made a cart of 3 + 2 read as "Cart (2)".
      var units = typeof cart.item_count === 'number' ? cart.item_count : 0;
      var want = base + ' (' + units + ')';
      if (head.textContent !== want) head.textContent = want;
    }

    // The close control is the theme's X glyph, centred on the heading's line by
    // the header row's own flexbox. Nothing to inject; any word label left over
    // from the earlier build is removed so it cannot reappear from cached markup.
    var stale = document.querySelector('#CartDrawer .pov-close-label');
    if (stale && stale.parentNode) stale.parentNode.removeChild(stale);

    // Denim renders several .totals rows in the drawer footer: an optional
    // savings row, an optional free text row, then the real order total last.
    // Relabelling the first one renamed the free text row ("Free") instead, so
    // the last one is the only correct target.
    var rows = document.querySelectorAll('#CartDrawer .cart-drawer__footer .totals');
    var total = rows.length ? rows[rows.length - 1].querySelector('.totals__total') : null;
    if (total && total.textContent.trim() !== 'Total') total.textContent = 'Total';

    // reassurance line above the checkout button. The class is the idempotence
    // guard: without it every repaint would rewrite the node, and rewriting it
    // is itself a mutation, which would retrigger the observer forever.
    var ctas = document.querySelector('#CartDrawer .cart__ctas');
    if (ctas) {
      if (!document.querySelector('#CartDrawer .pov-reassure')) {
        var note = document.querySelector('#CartDrawer .tax-note');
        if (note && !note.classList.contains('hidden')) {
          note.classList.add('pov-reassure');
          note.textContent = REASSURE;
        } else {
          var p = document.createElement('p');
          p.className = 'pov-reassure';
          p.textContent = REASSURE;
          ctas.parentNode.insertBefore(p, ctas);
        }
      }
      // the trust strip goes last, so it sits under the payment badges
      if (!ctas.querySelector('.pov-trust')) {
        var strip = document.createElement('div');
        strip.className = 'pov-trust';
        strip.innerHTML = TRUST.map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('');
        ctas.appendChild(strip);
      }
    }
  }

  /* ---------------------------------------------------------- suggestions */
  // A different category to everything already in the cart, cheapest first, with
  // bestsellers seeded so they always get a showing. Products with three or more
  // option dropdowns are skipped: the card grows too tall for the drawer.
  function choose(cart) {
    var areas = {}, have = {};
    (cart.items || []).forEach(function (i) {
      have[i.handle] = 1;
      var a = D.area[i.handle];
      if (a) areas[a] = 1;
    });

    var base = D.pool.filter(function (p) {
      return !areas[p.a] && !have[p.h] && (p.o == null || p.o <= 2);
    });
    if (!base.length) return [];

    var byArea = {};
    base.slice().sort(function (a, b) {
      return (b.b - a.b) || (a.p - b.p) || (a.n - b.n);
    }).forEach(function (p) { if (!byArea[p.a]) byArea[p.a] = p; });

    var reps = Object.keys(byArea).map(function (k) { return byArea[k]; })
      .sort(function (a, b) { return a.p - b.p; });

    var picked = reps.filter(function (p) { return p.b; }).slice(0, 3);
    for (var i = 0; i < reps.length && picked.length < 3; i++) {
      var p = reps[i];
      if (!picked.some(function (x) { return x.h === p.h || x.a === p.a; })) picked.push(p);
    }
    return picked.sort(function (a, b) { return a.p - b.p; });
  }

  function variantOf(prod, chosen) {
    if (!prod.variants || !prod.variants.length) return null;
    var hit = null;
    prod.variants.forEach(function (v) {
      if (hit) return;
      var ok = true;
      (prod.options || []).forEach(function (o, i) {
        if (chosen[i] && v.options[i] !== chosen[i]) ok = false;
      });
      if (ok) hit = v;
    });
    return hit || prod.variants[0];
  }

  function realOptions(prod) {
    // Shopify models "no options" as one option named Title / Default Title
    if (!prod.options || !prod.options.length) return [];
    if (prod.variants.length === 1 && /default title/i.test(prod.variants[0].title || '')) return [];
    return prod.options.map(function (o) {
      return typeof o === 'string' ? { name: o, values: [] } : o;
    });
  }

  /* The card is only rebuilt when the product it shows actually changes.

     Rebuilding it on every repaint is what closed the variant dropdown: a native
     select popup belongs to its DOM node, so replacing the node destroys the
     popup and the focus with it. Everything that can change without the product
     changing (price, chosen variant id, image, dot state) is written onto the
     existing nodes instead, and the <select> elements are never touched beyond
     correcting .value when it is already wrong. */
  function buildCard(prod, v, img, opts) {
    var pickers = !opts.length ? '' :
      '<div class="psg__vsel">' + opts.map(function (o, i) {
        return '<label><select data-opt="' + i + '">' + (o.values || []).map(function (val) {
          return '<option value="' + esc(val) + '"' + (sel[i] === val ? ' selected' : '') + '>' +
            esc(o.name + ': ' + val) + '</option>';
        }).join('') + '</select></label>';
      }).join('') + '</div>';

    var dots = list.length < 2 ? '' :
      '<div class="psg__dots">' + list.map(function (_, i) {
        return '<button type="button" class="' + (i === idx ? 'is-on' : '') + '" data-dot="' + i +
          '" aria-label="Suggestion ' + (i + 1) + '"></button>';
      }).join('') + '</div>';

    return '<div class="psg__card' + (busy ? ' is-busy' : '') + '" data-handle="' + esc(prod.handle) + '">' +
        '<div class="psg__ph"><img src="' + esc(img) + '" alt="" loading="lazy"></div>' +
        '<div class="psg__m">' +
          '<p class="psg__t">Want to add <span class="psg__name">' + esc(prod.title) + '</span>?</p>' +
          pickers +
          '<div class="psg__row"><span class="psg__p">' + money(v ? v.price : prod.price) + '</span>' +
          '<button type="button" class="psg__btn" data-variant="' + (v ? v.id : '') + '">Add to Cart</button>' +
          '</div>' +
        '</div>' +
      '</div>' + dots;
  }

  function patchCard(host, prod, v, img) {
    var im = host.querySelector('.psg__ph img');
    if (im && img && im.getAttribute('src') !== img) im.setAttribute('src', img);

    var priceEl = host.querySelector('.psg__p');
    var priceTxt = money(v ? v.price : prod.price);
    if (priceEl && priceEl.textContent !== priceTxt) priceEl.textContent = priceTxt;

    var btn = host.querySelector('.psg__btn');
    var vid = v ? String(v.id) : '';
    if (btn && btn.getAttribute('data-variant') !== vid) btn.setAttribute('data-variant', vid);

    var cardEl = host.querySelector('.psg__card');
    if (cardEl) cardEl.classList.toggle('is-busy', !!busy);

    // never rebuild the options, only correct a value that has drifted
    Array.prototype.forEach.call(host.querySelectorAll('select[data-opt]'), function (s2) {
      var i = Number(s2.getAttribute('data-opt'));
      if (sel[i] != null && s2.value !== sel[i]) s2.value = sel[i];
    });

    Array.prototype.forEach.call(host.querySelectorAll('[data-dot]'), function (b) {
      b.classList.toggle('is-on', Number(b.getAttribute('data-dot')) === idx);
    });
  }

  function renderSuggest(o) {
    o = o || {};
    var host = document.getElementById('PovSuggest');
    if (!host) return;
    if (!list.length) { host.hidden = true; stop(); return; }

    // A repaint requested from outside while the shopper has the dropdown open is
    // held, not dropped, and replayed the moment the menu closes.
    if (menuBusy() && !o.fromChange) { pendingRender = true; return; }

    var entry = list[idx];
    getProduct(entry.h).then(function (prod) {
      host = document.getElementById('PovSuggest');
      if (!host) return;
      if (!prod) { host.hidden = true; return; }

      // Re-check here, not only on the way in. getProduct is async, so a render
      // that passed the guard before the shopper touched the dropdown would
      // otherwise land while the menu is open and replace the card underneath
      // it. This is the check that actually protects an open menu.
      if (menuBusy() && !o.fromChange) { pendingRender = true; return; }

      var opts = realOptions(prod);
      if (!sel.length) sel = opts.map(function (x) { return x.values[0]; });
      var v = variantOf(prod, sel);
      var img = (v && v.featured_image && v.featured_image.src)
        ? v.featured_image.src
        : (prod.featured_image || (prod.images && prod.images[0]) || '');
      if (img) img = img.replace(/(\.[a-z]+)(\?|$)/i, '_200x200$1$2');

      var card = host.querySelector('.psg__card');
      var sameProduct = card && card.getAttribute('data-handle') === prod.handle;
      // dot count changing means the suggestion list itself changed
      var sameDots = host.querySelectorAll('[data-dot]').length === (list.length < 2 ? 0 : list.length);

      writing = true;
      if (sameProduct && sameDots) {
        patchCard(host, prod, v, img);
      } else {
        host.innerHTML = buildCard(prod, v, img, opts);
      }
      host.hidden = false;
      setTimeout(function () { writing = false; }, 80);
    }).catch(function () {});
  }

  /* ------------------------------------------------------------- rotation */
  function stop() { if (timer) { clearInterval(timer); timer = null; } }
  function start() {
    stop();
    if (manual || list.length < 2) return;
    var el = document.getElementById('PovSuggest');
    if (!el || el.hidden) return;
    timer = setInterval(function () {
      // Never advance out from under an open dropdown. The tick is skipped, not
      // deferred: the shopper gets the full interval again once the menu closes.
      if (menuBusy()) return;
      idx = (idx + 1) % list.length;
      sel = [];
      renderSuggest();
    }, ROTATE_MS);
  }

  /* --------------------------------------------------------------- events */
  // Swapping the carousel state is separate from painting it, because a cart
  // read that lands while the dropdown is open must not move the shopper off the
  // product they are configuring, or reset the option they have already chosen.
  function applyList(next) {
    var changed = next.map(function (p) { return p.h; }).join(',') !==
      list.map(function (p) { return p.h; }).join(',');
    list = next;
    if (changed || idx >= list.length) { idx = 0; sel = []; }
  }

  function onCart(cart) {
    try {
      lastCart = cart;
      writing = true;
      renderChrome(cart);
      var next = choose(cart);

      // This was the second half of the dropdown bug. The render was already
      // being deferred, but list/idx/sel were still being reassigned underneath
      // it, so the deferred paint came back showing a different product and with
      // the shopper's chosen option thrown away.
      if (menuBusy() || manual) {
        deferredList = next;
        pendingRender = true;
        return;
      }

      applyList(next);
      renderSuggest();
      start();
    } catch (e) {
      var el = document.getElementById('PovSuggest');
      if (el) el.hidden = true;
    } finally {
      setTimeout(function () { writing = false; }, 80);
    }
  }

  function refresh() {
    return fetch(routes().cart, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(onCart)
      .catch(function () {});
  }

  function addChosen(btn) {
    if (busy) return;
    var id = btn.getAttribute('data-variant');
    if (!id) return;
    busy = true;
    var card = btn.closest('.psg__card');
    if (card) card.classList.add('is-busy');

    fetch(routes().add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        // ask Denim to hand back the drawer markup so its own renderer can swap it in
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        busy = false;
        manual = false;
        var drawer = document.querySelector('cart-drawer');
        if (drawer && state && state.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(state);          // Denim repaints its own line items
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
        return refresh();
      })
      .catch(function () {
        busy = false;
        renderSuggest();
      });
  }

  /* ----------------------------------------------------------------- mount */
  var bound = false;
  function bindOnce() {
    if (bound) return;
    bound = true;

    // Delegated from document, so it survives every repaint of .drawer__inner.
    // The drawer contains a <form> and Denim binds its own click handlers, so
    // both default and propagation are stopped or these buttons submit it.
    document.addEventListener('click', function (e) {
      var host = e.target.closest && e.target.closest('#PovSuggest');
      if (!host) return;
      var b = e.target.closest('button');
      if (!b) return;
      e.preventDefault();
      e.stopPropagation();
      if (b.classList.contains('psg__btn')) return addChosen(b);
      if (b.hasAttribute('data-dot')) {
        manual = true; stop();                   // manual control wins for the session
        var n = Number(b.getAttribute('data-dot'));
        if (n !== idx) { idx = n; sel = []; renderSuggest(); }
      }
    }, true);

    // Opening the dropdown marks the card busy; every way out of it releases.
    document.addEventListener('pointerdown', function (e) {
      if (e.target.closest && e.target.closest('#PovSuggest select')) {
        menuOpen = true;
        stop();                                   // freeze the carousel while it is open
        syncIdxToCard();
      }
    }, true);

    document.addEventListener('focusin', function (e) {
      if (e.target.closest && e.target.closest('#PovSuggest select')) {
        menuOpen = true;
        stop();
        syncIdxToCard();
      }
    });

    document.addEventListener('focusout', function (e) {
      if (e.target.closest && e.target.closest('#PovSuggest select')) {
        setTimeout(releaseMenu, 0);               // after focus has settled
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menuOpen) releaseMenu();
    });

    document.addEventListener('change', function (e) {
      var host = e.target.closest && e.target.closest('#PovSuggest');
      if (!host) return;
      var s = e.target.closest('select');
      if (!s) return;
      syncIdxToCard();          // the choice belongs to the card on screen
      sel[Number(s.getAttribute('data-opt'))] = s.value;
      menuOpen = false;
      // Choosing an option is deliberate engagement with this card, so the
      // carousel stops and any held list is dropped. Without this the card could
      // flip to another product between the choice and the Add to Cart click,
      // and the shopper would add something they never configured.
      manual = true;
      stop();
      deferredList = null;
      pendingRender = false;
      // fromChange patches in place, so price and variant id follow the choice
      // without the <select> being rebuilt underneath it
      renderSuggest({ fromChange: true });
    });

    document.addEventListener('cart:updated', function () { refresh(); });
    document.addEventListener('cart:open', function () {
      if (lastCart) renderChrome(lastCart);
      start();
    });
    document.addEventListener('cart:close', stop);

    // The quantity stepper is the case that used to go stale: Denim repaints the
    // drawer from the cart-drawer section without always dispatching cart:updated,
    // so repainting from the cart we happened to be holding showed the count from
    // before the change. Every stepper interaction now re-reads /cart.js.
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('#CartDrawer .quantity__button, #CartDrawer cart-remove-button')) {
        setTimeout(function () { if (!menuBusy()) refresh(); }, 700);
        setTimeout(function () { if (!menuBusy()) refresh(); }, 1800);
      }
    });
    document.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest('#CartDrawer .quantity__input')) {
        setTimeout(refresh, 700);
      }
    });

    // Denim replaces the whole .drawer__inner on every cart change, which wipes
    // the injected chrome and the suggestion card, so watch and repaint. The
    // repaint re-reads the cart rather than reusing the held copy, for the same
    // staleness reason; `writing` keeps our own DOM writes from retriggering it.
    var host = document.getElementById('CartDrawer');
    if (host && window.MutationObserver) {
      var pending = null;
      var lastRead = 0;

      // Our own writes land inside this same subtree, so an unfiltered observer
      // reacted to itself: a card render triggered a refresh, which rendered the
      // card again, which triggered another refresh. That loop is what replaced
      // the card roughly twice a second and closed the dropdown. Mutations that
      // originate in the nodes this file writes are ignored; only Denim's own
      // repaints get through.
      var ours = function (n) {
        var el = n && n.nodeType === 1 ? n : (n && n.parentElement);
        if (!el || !el.closest) return false;
        return !!(el.closest('#PovSuggest') || el.closest('.pov-trust') ||
                  el.closest('.pov-payments') || el.closest('.drawer__heading') ||
                  el.closest('.pov-reassure'));
      };

      new MutationObserver(function (muts) {
        if (writing) return;
        var relevant = false;
        for (var i = 0; i < muts.length; i++) {
          if (!ours(muts[i].target)) { relevant = true; break; }
        }
        if (!relevant) return;

        clearTimeout(pending);
        pending = setTimeout(function () {
          // never repaint out from under an open dropdown
          if (menuBusy()) { pendingRender = true; return; }
          var now = Date.now();
          if (now - lastRead > 500) { lastRead = now; refresh(); }
          else { if (lastCart) renderChrome(lastCart); renderSuggest(); }
        }, 200);
      }).observe(host, { childList: true, subtree: true });
    }
  }

  function mount() {
    if (!document.getElementById('CartDrawer')) return;
    bindOnce();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
